<template>
  <div class="testing-panel p-3 m-3 border rounded">
    <h5 class="mb-3">
      🧪 Deployment Detection Testing Panel
    </h5>
    <p class="text-muted mb-3">
      Use these buttons to simulate deployment scenarios in development
    </p>

    <div class="row">
      <div class="col-md-6 mb-3">
        <b-card>
          <b-card-title>Service Worker Tests</b-card-title>
          <div class="d-flex flex-column gap-2">
            <b-button
              variant="primary"
              size="sm"
              :disabled="isSimulating"
              @click="simulateServiceWorkerUpdate"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="14"
                class="mr-1"
              />
              Simulate SW Update
            </b-button>

            <b-button
              variant="info"
              size="sm"
              @click="checkServiceWorkerStatus"
            >
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-1"
              />
              Check SW Status
            </b-button>

            <b-button
              variant="warning"
              size="sm"
              @click="forceServiceWorkerUpdate"
            >
              <feather-icon
                icon="ZapIcon"
                size="14"
                class="mr-1"
              />
              Force SW Update
            </b-button>
          </div>
        </b-card>
      </div>

      <div class="col-md-6 mb-3">
        <b-card>
          <b-card-title>Bundle Tests</b-card-title>
          <div class="d-flex flex-column gap-2">
            <b-button
              variant="success"
              size="sm"
              :disabled="isSimulating"
              @click="simulateBundleChange"
            >
              <feather-icon
                icon="PackageIcon"
                size="14"
                class="mr-1"
              />
              Simulate Bundle Change
            </b-button>

            <b-button
              variant="secondary"
              size="sm"
              @click="checkCurrentBundles"
            >
              <feather-icon
                icon="FileTextIcon"
                size="14"
                class="mr-1"
              />
              Check Current Bundles
            </b-button>

            <b-button
              variant="outline-primary"
              size="sm"
              @click="manualDeploymentCheck"
            >
              <feather-icon
                icon="SearchIcon"
                size="14"
                class="mr-1"
              />
              Manual Check
            </b-button>
          </div>
        </b-card>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <b-card>
          <b-card-title>Direct Notification Tests</b-card-title>
          <div class="d-flex flex-wrap gap-2">
            <b-button
              variant="gradient-primary"
              size="sm"
              @click="showTestNotification"
            >
              <feather-icon
                icon="BellIcon"
                size="14"
                class="mr-1"
              />
              Show Test Notification
            </b-button>

            <b-button
              variant="gradient-success"
              size="sm"
              @click="simulateVersionChange"
            >
              <feather-icon
                icon="GitBranchIcon"
                size="14"
                class="mr-1"
              />
              Simulate Version Change
            </b-button>

            <b-button
              variant="gradient-warning"
              size="sm"
              @click="resetDetectionState"
            >
              <feather-icon
                icon="RotateCcwIcon"
                size="14"
                class="mr-1"
              />
              Reset Detection State
            </b-button>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Status Display -->
    <div
      v-if="statusMessage"
      class="mt-3"
    >
      <b-alert
        :variant="statusVariant"
        show
        dismissible
        @dismissed="statusMessage = ''"
      >
        {{ statusMessage }}
      </b-alert>
    </div>

    <!-- Console Log -->
    <div class="mt-3">
      <b-card>
        <b-card-title>Console Log</b-card-title>
        <div
          ref="consoleLog"
          class="console-log"
        >
          <div
            v-for="(log, index) in consoleLogs"
            :key="index"
            :class="['log-entry', `log-${log.type}`]"
          >
            <span class="log-time">{{ log.time }}</span>
            <span class="log-message">{{ log.message }}</span>
          </div>
        </div>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mt-2"
          @click="clearLogs"
        >
          Clear Logs
        </b-button>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BButton, BAlert,
} from 'bootstrap-vue'
import deploymentDetector from '@/utils/deploymentDetector'

export default {
  name: 'DeploymentTestingPanel',
  components: {
    BCard,
    BCardTitle,
    BButton,
    BAlert,
  },
  data() {
    return {
      isSimulating: false,
      statusMessage: '',
      statusVariant: 'info',
      consoleLogs: [],
    }
  },
  mounted() {
    this.addLog('info', 'Testing panel loaded')

    // Override console methods to capture logs
    this.originalConsoleLog = console.log
    this.originalConsoleError = console.error

    console.log = (...args) => {
      this.addLog('info', args.join(' '))
      this.originalConsoleLog(...args)
    }

    console.error = (...args) => {
      this.addLog('error', args.join(' '))
      this.originalConsoleError(...args)
    }
  },
  beforeDestroy() {
    // Restore original console methods
    console.log = this.originalConsoleLog
    console.error = this.originalConsoleError
  },
  methods: {
    addLog(type, message) {
      this.consoleLogs.push({
        type,
        message,
        time: new Date().toLocaleTimeString(),
      })

      // Auto-scroll to bottom
      this.$nextTick(() => {
        if (this.$refs.consoleLog) {
          this.$refs.consoleLog.scrollTop = this.$refs.consoleLog.scrollHeight
        }
      })
    },

    clearLogs() {
      this.consoleLogs = []
    },

    setStatus(message, variant = 'info') {
      this.statusMessage = message
      this.statusVariant = variant
      this.addLog(variant, message)
    },

    async simulateServiceWorkerUpdate() {
      this.isSimulating = true
      this.setStatus('Simulating service worker update...', 'info')

      try {
        // Force service worker update
        if (deploymentDetector.registration) {
          await deploymentDetector.registration.update()
          this.setStatus('Service worker update triggered', 'success')
        } else {
          this.setStatus('No service worker registered yet', 'warning')
        }
      } catch (error) {
        this.setStatus(`Error: ${error.message}`, 'danger')
      } finally {
        this.isSimulating = false
      }
    },

    async checkServiceWorkerStatus() {
      if (!deploymentDetector.registration) {
        this.setStatus('No service worker registered', 'warning')
        return
      }

      const sw = deploymentDetector.registration
      const status = {
        scope: sw.scope,
        installing: !!sw.installing,
        waiting: !!sw.waiting,
        active: !!sw.active,
        updatefound: 'Event listener attached',
      }

      this.setStatus(`SW Status: ${JSON.stringify(status, null, 2)}`, 'info')
    },

    async forceServiceWorkerUpdate() {
      this.setStatus('Forcing service worker update...', 'info')

      try {
        // Unregister current service worker
        if (deploymentDetector.registration) {
          await deploymentDetector.registration.unregister()
          this.addLog('info', 'Service worker unregistered')
        }

        // Wait a bit then re-register
        setTimeout(async () => {
          await deploymentDetector.init()
          this.setStatus('Service worker re-registered', 'success')
        }, 1000)
      } catch (error) {
        this.setStatus(`Error: ${error.message}`, 'danger')
      }
    },

    async simulateBundleChange() {
      this.isSimulating = true
      this.setStatus('Simulating bundle change detection...', 'info')

      try {
        // Call the bundle check method directly
        await deploymentDetector.checkBundleVersion()
        this.setStatus('Bundle check completed', 'success')
      } catch (error) {
        this.setStatus(`Error: ${error.message}`, 'danger')
      } finally {
        this.isSimulating = false
      }
    },

    checkCurrentBundles() {
      const scripts = Array.from(document.querySelectorAll('script[src*="app."]')).map(script => script.src.split('/').pop())

      this.setStatus(`Current bundles: ${scripts.join(', ')}`, 'info')
    },

    async manualDeploymentCheck() {
      this.setStatus('Running manual deployment check...', 'info')
      await deploymentDetector.manualCheck()
      this.setStatus('Manual check completed', 'success')
    },

    showTestNotification() {
      this.setStatus('Showing test notification...', 'info')
      deploymentDetector.showDeploymentNotification('1.2.0-test')
    },

    simulateVersionChange() {
      this.setStatus('Simulating version change...', 'info')

      // Mock a version change
      const oldVersion = deploymentDetector.version
      deploymentDetector.version = '1.2.0-test'

      this.addLog('info', `Version changed from ${oldVersion} to ${deploymentDetector.version}`)
      deploymentDetector.showDeploymentNotification()

      this.setStatus('Version change notification triggered', 'success')
    },

    resetDetectionState() {
      deploymentDetector.hasNewDeployment = false
      this.setStatus('Detection state reset', 'success')
    },
  },
}
</script>

<style scoped>
.testing-panel {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6 !important;
}

.gap-2 {
  gap: 0.5rem;
}

.console-log {
  background: #1e1e1e;
  color: #fff;
  padding: 15px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  height: 200px;
  overflow-y: auto;
}

.log-entry {
  margin-bottom: 5px;
  display: block;
}

.log-time {
  color: #888;
  margin-right: 10px;
}

.log-info .log-message {
  color: #4caf50;
}

.log-error .log-message {
  color: #f44336;
}

.log-warning .log-message {
  color: #ff9800;
}
</style>
